import {
    Rule,
    SelectionSourceScreen,
    SelectionSourceType,
    SelectionType,
} from '@superbet-group/web.types';

import ISelection from '@core/models/betSlip/ISelection';
import ISerializedSelection from '@core/models/betSlip/serialization/ISerializedSelection';
import Odd from '@core/models/offer/Odd';
import OfferEvent from '@core/models/offer/OfferEvent';

import BetSlipOdd from '../base/BetSlipOdd';
import ISportlikeEvent from '../base/ISportlikeEvent';
import { isSuperbetByName } from '@superbet-group/web.lib.vue-utilities';

let uniqueSelectionId = 1;
export default class SportSelection implements ISelection {
    event: ISportlikeEvent;
    // Event can come from offer without odd that is already selected!
    odd: BetSlipOdd | null;
    isFixed = false;
    isShowFix = false;
    type: SelectionType = SelectionType.sport;
    sourceType: SelectionSourceType;
    sourceScreen: SelectionSourceScreen;
    index?: number;
    public readonly uniqueSelectionId: number;

    constructor() {
        // eslint-disable-next-line no-plusplus
        this.uniqueSelectionId = uniqueSelectionId++;
    }

    public toggleFixState(): void {
        this.isFixed = !this.isFixed;
    }

    public getEventId(): number {
        return this.event.id;
    }

    public getMarketId(): number | null {
        return this.odd ? this.odd.marketId : null;
    }

    public getTags(): string {
        return this.odd?.tags || '';
    }

    public getOddDescription(): string {
        return this.odd ? this.odd.description : '';
    }

    public getRules(): Rule[] {
        return this.event.rules;
    }

    public getOddName(): string {
        return this.odd ? this.odd.name : '';
    }

    public getOddValue(): number | null {
        return this.odd ? this.odd.value : null;
    }

    public getOddId(): number | null {
        return this.odd ? this.odd.id : null;
    }

    public getOddUniqueId(): string | null {
        return this.odd ? (this.odd as Odd).uniqueId : null;
    }

    public getOddUuid(): string | null {
        return this.odd ? this.odd.uuid : null;
    }

    getTournamentId(): number | undefined {
        return this.event.tournamentId;
    }

    getTournamentName(): string | undefined {
        return this.event.tournamentName;
    }

    getSportName(): string | undefined {
        return this.event.sportName;
    }

    getEventDate(): Date {
        return this.event.date;
    }

    getEventFullName(): string {
        return this.event.fullName;
    }

    public getOddSpecialBetValue(): string {
        return this.odd ? this.odd.specialBetValue : '';
    }

    public isEventFinished(): boolean {
        return this.event.isFinished();
    }

    public isOngoing(): boolean {
        return this.event.isOngoing();
    }

    public isSportType(): boolean {
        return this.getItemType() === SelectionType.sport;
    }

    public isBonusable(): boolean {
        return !!this.odd && this.odd.isBonusable;
    }

    public isPromotional(): boolean {
        return !!this.odd && this.odd.isInPromotion;
    }

    hasOdd(odd: BetSlipOdd | null): boolean {
        if (this.odd === odd) {
            return true;
        }
        if (!this.odd || !odd) {
            return false;
        }

        return this.odd.id === odd.id && this.odd.specialBetValue === odd.specialBetValue;
    }

    // TODO can be a getter but not necessary
    public isLocked(): boolean {
        return this.odd ? this.odd.isLocked || this.event.isLocked : false;
    }

    public hasEvent(eventId: number, type: SelectionType = SelectionType.sport): boolean {
        return this.getEventId() === eventId && this.getItemType() === type;
    }

    public exists(): boolean {
        return !!this.odd && !this.odd.isFinished;
    }

    public isEqual(compareItem: ISelection): boolean {
        return this.hasEvent(compareItem.getEventId(), compareItem.getItemType()) && this.hasOdd(compareItem.odd);
    }

    public serialize(): ISerializedSelection {
        return {
            eventId: this.getEventId(),
            oddId: this.getOddId(),
            stashedOdd: this.odd,
            isFixed: this.isFixed,
            isShowFix: this.isShowFix,
            itemType: this.getItemType(),
            specialBetValue: this.getOddSpecialBetValue(),
            oddValue: this.getOddValue(),
            offerId: this.getOfferId(),
            sourceType: this.sourceType,
            sourceScreen: this.sourceScreen,
        };
    }

    // TODO move to getters
    getOfferId(): number | undefined {
        return this.event.offerId;
    }

    getItemType(): SelectionType {
        return this.type;
    }

    // TODO don't like
    getEvent(): ISportlikeEvent {
        return this.event;
    }

    // TODO don't like
    getOdd(): BetSlipOdd {
        return this.odd as BetSlipOdd;
    }

    // TODO don't like
    setOddValue(value: number): void {
        if (this.odd) {
            this.odd.value = value;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    isAvailableForBetting(): boolean {
        return true;
    }

    getBettingPeriod(): { betStart: Date; betEnd: Date } {
        return {
            betStart: new Date(), // or further in the past, but it won't make a difference
            betEnd: this.event.date,
        };
    }

    get negotiationName(): string {
        return this.event.fullName;
    }

    get isGoingToHaveLiveVideo(): boolean {
        if (this.isSportType()) {
            return (<OfferEvent> this.event).isGoingToHaveLiveVideo || false;
        }
        return false;
    }

    get name(): string {
        return this.odd ? this.odd.marketName : '';
    }

    get isGoingToBeInLiveBetting(): boolean {
        if (this.isSportType()) {
            return (<OfferEvent> this.event).isGoingToBeInLiveBetting || false;
        }
        return false;
    }

    duplicate(): SportSelection {
        return SportSelection.createItem(this.event, this.odd, this.sourceType);
    }

    public static createItem(
        event: ISportlikeEvent,
        odd: BetSlipOdd | null,
        sourceType?: SelectionSourceType,
        sourceScreen?: SelectionSourceScreen,
        index?: number,
    ): SportSelection {
        let item = new SportSelection();

        item.event = event;
        item.odd = odd;
        item.sourceScreen = sourceScreen ?? SelectionSourceScreen.Unknown;
        item.index = index;

        if (sourceType !== undefined) {
            item.sourceType = sourceType;
        } else if (odd) {
            item.sourceType = isSuperbetOrBetBoostedSuperbet(odd)
                ? SelectionSourceType.SuperBets
                : SelectionSourceType.Regular;
        } else {
            item.sourceType = SelectionSourceType.Unknown;
        }
        return item;
    }
}

// Across the app, boosted odds are not treated as superbet odds,
// even if they _are_ superbets. Therefore, we need an extra check here.
// TODO: Consider refactoring this clause, since we're doing this in another place
// already. Maybe a cleaner distinction between isSuperbetForLayout and isSuperbetForOtherPurposes
// would be more appropriate.
function isSuperbetOrBetBoostedSuperbet(odd: BetSlipOdd): boolean {
    return odd.isSuperbet || (!!odd.name && isSuperbetByName(odd.name));
}
